import React, { FunctionComponent } from 'react';

import styled from 'styled-components';

import { Translate } from 'react-localize-redux';

import Typography from '@components/basics/typography';
import MotionOpacity from '@components/motion/motion-opacity';

import { ItemDetail } from './detail-item';
import DineroFactory from 'dinero.js';

interface InfoItemProps {
  className?: string;
  subtitle?: React.ReactNode;
  items?: ItemDetail[];
  isSimplified?: boolean;
}

const dineroFormatter = (earnedItem: DineroFactory.Dinero) => {
  return earnedItem.toFormat();
};

const InfoItem: FunctionComponent<InfoItemProps> = ({
  className,
  items,
  isSimplified,
}) => {
  return (
    <MotionOpacity className={className || ''}>
      <OpenWrapper className={isSimplified ? 'simplified' : ''}>
        {items &&
          items.map((item, i) => (
            <Row key={i}>
              <div
                className={`main-row ${item.subitems ? 'has-subitems' : ''}`}
              >
                <Typography className="info-item-text">{item.label}</Typography>
                <TwoColumnGrid className="gap">
                  {(item.sales || item.streams) && (
                    <TwoColumnGrid className="wrap">
                      <Typography className="info-item-text" customFont="bold">
                        <Translate
                          id={
                            item.sales
                              ? 'EARNINGS.DETAILS.ITEM.DETAIL.SALES'
                              : 'EARNINGS.DETAILS.ITEM.DETAIL.STREAMS'
                          }
                        />
                      </Typography>
                      <Typography className="info-item-text" customFont="bold">
                        {item.sales ? item.sales : item.streams}
                      </Typography>
                    </TwoColumnGrid>
                  )}
                  <TwoColumnGrid className="wrap">
                    <Typography className="info-item-text" type="small">
                      {dineroFormatter(item.earned)}
                    </Typography>
                  </TwoColumnGrid>
                </TwoColumnGrid>
              </div>
              {item.subitems &&
                item.subitems?.map((subitem, i) => (
                  <Subitem key={i} className="sub-row">
                    <Typography className="info-item-label" type="small">
                      {subitem.label}
                    </Typography>
                    <TwoColumnGrid className="gap">
                      <TwoColumnGrid className="wrap">
                        <Typography className="info-item-text" type="small">
                          <Translate
                            id={
                              subitem.sales
                                ? 'EARNINGS.DETAILS.ITEM.DETAIL.SALES'
                                : 'EARNINGS.DETAILS.ITEM.DETAIL.STREAMS'
                            }
                          />
                        </Typography>
                        <Typography className="info-item-text" type="small">
                          {subitem.streams}
                        </Typography>
                      </TwoColumnGrid>
                      <TwoColumnGrid className="wrap">
                        <Typography className="info-item-text" type="small">
                          <Translate id="EARNINGS.DETAILS.ITEM.DETAIL.EARNED" />
                        </Typography>
                        <Typography className="info-item-text" type="small">
                          {subitem.earned.toFormat()}
                        </Typography>
                      </TwoColumnGrid>
                    </TwoColumnGrid>
                  </Subitem>
                ))}
            </Row>
          ))}
      </OpenWrapper>
    </MotionOpacity>
  );
};

export default InfoItem;

const OpenWrapper = styled.div`
  margin-block-start: 15px;

  @media ${({ theme }) => theme.device.tablet} {
    padding-inline-end: 48px;
  }

  &.simplified {
    margin: 0;
  }

  .info-item-subtitle,
  .info-item-text {
    font-size: 14px;
    font-weight: 600;

    color: ${({ theme }) => theme.colors.black};
  }
`;

const Row = styled.div`
  padding-block: 15px;

  .main-row,
  .sub-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: center;
  }

  .main-row {
    &.has-subitems {
      margin-block-end: 5px;
    }
  }
`;

const Subitem = styled.div`
  padding-block-end: 5px;
  border-left: 1px solid ${({ theme }) => theme.colors.border};

  .info-item-label {
    padding-inline-start: 20px;
  }

  &:last-child {
    padding-block-end: 0;
  }
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  &.gap {
    grid-gap: 10px;
  }

  &.wrap {
    grid-template-columns: minmax(0, 1fr);
    @media ${({ theme }) => theme.device.tablet} {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  .info-item-text {
    text-align: right;
  }
`;
